/**
 * `applyTheme` (W5-F1-S2) — write per-partner CSS variables onto
 * `document.documentElement.style` so every shadcn / Tailwind utility
 * re-themes without touching components.
 *
 * Senviro's design tokens (W1-F7-S3, see `apps/web/src/styles/globals.css`)
 * are HSL triplets in the shadcn convention: `--primary: 165 56% 28%` consumed
 * as `hsl(var(--primary))`. The `tenant_branding` row stores hex strings
 * (`#1F6E5C`); this module converts hex → `H S% L%` and writes the result.
 *
 * Variables overridden:
 *   --primary, --primary-foreground, --ring  (driven by color_primary)
 *   --accent,  --accent-foreground            (driven by color_accent)
 *   --font-display                            (driven by font_heading)
 *   --font-body                               (driven by font_body)
 *
 * Foregrounds are auto-derived: white on dark backgrounds, near-black on
 * light. Saves the partner from picking a contrast-correct foreground for
 * every brand color.
 *
 * Reset behaviour: passing `null` (or a default-id branding) removes the
 * inline overrides so the `:root` defaults from globals.css take over.
 */

import type { TenantBranding } from '@senviro/core-api-client';

/** Variable names we touch; centralised so tests can assert + reset. */
const MANAGED_VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--accent',
  '--accent-foreground',
  '--font-display',
  '--font-body',
] as const;

export type ManagedVar = (typeof MANAGED_VARS)[number];

export function applyTheme(branding: TenantBranding | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Reset path — when no branding (or platform-defaults id) is supplied,
  // strip overrides so :root defaults from globals.css take over.
  if (!branding || branding.id === 'senviro-platform-defaults') {
    for (const v of MANAGED_VARS) root.style.removeProperty(v);
    return;
  }

  if (branding.color_primary) {
    const hsl = hexToHsl(branding.color_primary);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      // Ring is driven by the brand color in shadcn — keeps focus rings
      // visually consistent with the partner's accent.
      root.style.setProperty('--ring', hsl);
      // Foreground = white on dark / near-black on light, computed from
      // the same HSL so contrast stays AA-readable for any color.
      root.style.setProperty(
        '--primary-foreground',
        readableForeground(hsl),
      );
    }
  }

  if (branding.color_accent) {
    const hsl = hexToHsl(branding.color_accent);
    if (hsl) {
      root.style.setProperty('--accent', hsl);
      root.style.setProperty(
        '--accent-foreground',
        readableForeground(hsl),
      );
    }
  }

  if (branding.font_heading) {
    root.style.setProperty('--font-display', branding.font_heading);
  }

  if (branding.font_body) {
    root.style.setProperty('--font-body', branding.font_body);
  }
}

/**
 * Convert `#RRGGBB` (or `#RGB`) into the shadcn-style HSL triplet
 * `H S% L%` (e.g. `'165 56% 28%'`). Returns `null` for unrecognised input.
 *
 * Algorithm: standard RGB→HSL conversion (see CSS Color Module L3 §6.4).
 * H is rounded to the nearest integer; S and L are rounded to whole
 * percent — sufficient resolution for partner branding, avoids unstable
 * cascade churn from sub-percent jitter.
 */
export function hexToHsl(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { r, g, b } = rgb;
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN:
        h = (gN - bN) / d + (gN < bN ? 6 : 0);
        break;
      case gN:
        h = (bN - rN) / d + 2;
        break;
      case bN:
        h = (rN - gN) / d + 4;
        break;
    }
    h /= 6;
  }
  const hDeg = Math.round(h * 360);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);
  return `${hDeg} ${sPct}% ${lPct}%`;
}

/** Pick a readable foreground (white or near-black) for an HSL background.
 *  Uses the L (lightness) channel as the proxy — `>= 60%` means light, so
 *  use a dark foreground; otherwise white. Tuned to be conservative; AA
 *  contrast holds for the common Senviro brand-color spectrum. */
export function readableForeground(hsl: string): string {
  const m = /^\d+\s+\d+%\s+(\d+)%$/.exec(hsl);
  if (!m) return '0 0% 100%';
  const lPct = Number(m[1]);
  return lPct >= 60 ? '222 47% 11%' : '0 0% 100%';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hex);
  if (m3) {
    const [, r, g, b] = m3 as unknown as [string, string, string, string];
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16),
    };
  }
  const m6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (m6) {
    const [, r, g, b] = m6 as unknown as [string, string, string, string];
    return {
      r: parseInt(r, 16),
      g: parseInt(g, 16),
      b: parseInt(b, 16),
    };
  }
  return null;
}

export const __test__ = { MANAGED_VARS };
